import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import ConfirmationModal from './components/ConfirmationModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import UpdateProfileModal from './components/UpdateProfileModal';
import NewRecordModal from './components/NewRecordModal';
import EditRecordModal from './components/EditRecordModal';
import { records } from './utils/api';
import VerificationBanner from './components/VerificationBanner';
import { useToast } from './components/Toast';
import LegalFooter from './components/LegalFooter';
import { useConfirmation } from './hooks/useConfirmation';
import useAppSession from './hooks/useAppSession';
import useRecordTransforms from './hooks/useRecordTransforms';
import useAppActions from './hooks/useAppActions';
import MainViewContainer from './containers/MainViewContainer';
import AuthFlowContainer from './containers/AuthFlowContainer';

export default function App() {
  const [currentView, setCurrentView] = useState('home');
  const [recordsList, setRecordsList] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [providersList, setProvidersList] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const toast = useToast();
  const [showNewRecordModal, setShowNewRecordModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const { confirmModal, showConfirmation, closeConfirmation } = useConfirmation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);
  const {
    mapRecordType,
    normalizeAccessLevel,
    transformRecord,
    transformPermission
  } = useRecordTransforms();

  /** Purpose: Clears query/hash state from browser URL. Inputs: none. Output: none. */
  const clearUrlQueryState = useCallback(() => {
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  /** Purpose: Syncs URL query to current SPA view. Inputs: nextView, importSection?. Output: none. */
  const syncViewUrl = useCallback((nextView, importSection = null) => {
    const params = new URLSearchParams(window.location.search);
    params.set('view', nextView);
    if (nextView === 'import' && importSection) {
      params.set('section', importSection);
    } else {
      params.delete('section');
    }
    const nextQuery = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`);
  }, []);

  /** Purpose: Opens legal notices route. Inputs: none. Output: none. */
  const handleOpenLegal = useCallback(() => {
    window.location.assign('/legal');
  }, []);

  const handleSessionLogoutCleanup = useCallback(() => {
    setCurrentView('home');
    setRecordsList([]);
    setPermissionsList([]);
    setProvidersList([]);
    setAuditLogs([]);
  }, []);

  const {
    user,
    setUser,
    sessionStartRef,
    firstUploadTrackedRef,
    emailVerified,
    showVerificationBanner,
    setShowVerificationBanner,
    isLoading,
    error,
    authView,
    setAuthView,
    resetToken,
    setResetToken,
    verifyToken,
    setVerifyToken,
    handleLogin,
    handleLogout,
    handleResendVerification,
    loadData,
    refreshData
  } = useAppSession({
    transformRecord,
    transformPermission,
    setRecordsList,
    setPermissionsList,
    setProvidersList,
    setAuditLogs,
    clearUrlQueryState,
    toast,
    onLogoutCleanup: handleSessionLogoutCleanup
  });

  const handleViewChange = useCallback((nextView, options = {}) => {
    setCurrentView(nextView);
    syncViewUrl(nextView, options.importSection || null);
    if (user) {
      refreshData();
    }
  }, [syncViewUrl, user, refreshData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    if (viewParam) {
      const viewMap = {
        dashboard: 'records'
      };
      const normalizedView = viewMap[viewParam] || viewParam;
      const allowedViews = ['home', 'records', 'providers', 'permissions', 'import', 'audit', 'account'];
      if (allowedViews.includes(normalizedView)) {
        setCurrentView(normalizedView);
      }
    }
  }, []);

  const {
    handleDeleteRecord,
    handleGrantPermission,
    handleRevokePermission,
    handleAddProvider,
    handleUpdateProvider,
    handleUploadModalSubmit: submitUploadModal,
    handleViewRecord
  } = useAppActions({
    recordsList,
    permissionsList,
    providersList,
    setRecordsList,
    setPermissionsList,
    setProvidersList,
    loadData,
    mapRecordType,
    normalizeAccessLevel,
    transformRecord,
    transformPermission,
    showConfirmation,
    closeConfirmation,
    toast,
    sessionStartRef,
    firstUploadTrackedRef
  });

  const handleEditRecord = useCallback((record) => {
    setEditingRecord(record);
  }, []);

  const handleUpdateRecord = useCallback(async (recordId, updatedData) => {
    try {
      const backendRecord = {
        title: updatedData.title,
        provider: updatedData.provider,
        facility: updatedData.facility,
        recordDate: updatedData.date,
        recordType: mapRecordType(updatedData.type),
        description: updatedData.description,
        tags: updatedData.tags || []
      };

      const updated = await records.update(recordId, backendRecord);
      setRecordsList((prev) => prev.map((r) =>
        r.id === recordId ? transformRecord(updated) : r
      ));
      setEditingRecord(null);
      toast.success('Record updated');
    } catch (err) {
      console.error('Failed to update record:', err);
      throw err;
    }
  }, [mapRecordType, setRecordsList, toast, transformRecord]);

  const handleUploadModalSubmit = useCallback(async (newRecord) => {
    const created = await submitUploadModal(newRecord);
    if (created) {
      setShowNewRecordModal(false);
    }
  }, [submitUploadModal]);

  // Show auth page if not logged in
  if (!user && !isLoading) {
    return (
      <AuthFlowContainer
        verifyToken={verifyToken}
        onClearVerifyToken={() => setVerifyToken(null)}
        authView={authView}
        onSetAuthView={setAuthView}
        resetToken={resetToken}
        onClearResetToken={() => setResetToken(null)}
        onLogin={handleLogin}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-14 h-14 border-[3px] border-brand-100 border-t-brand-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm font-medium">Loading MedCaddy...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-gradient">
      <Header 
        currentView={currentView} 
        setCurrentView={handleViewChange}
        permissions={permissionsList}
        auditLogs={auditLogs}
        onLogout={handleLogout}
        onChangePassword={() => setShowChangePassword(true)}
        onUpdateProfile={() => setShowUpdateProfile(true)}
        onGoToAccount={() => handleViewChange('account')}
        onOpenLegal={handleOpenLegal}
      />
      
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
          <div className="bg-red-50/80 border border-red-200/60 text-red-600 px-4 py-3 rounded-2xl text-sm">
            {error}
          </div>
        </div>
      )}

      {showVerificationBanner && !emailVerified && (
        <VerificationBanner onDismiss={() => setShowVerificationBanner(false)} />
      )}
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <MainViewContainer
          currentView={currentView}
          recordsList={recordsList}
          permissionsList={permissionsList}
          providersList={providersList}
          auditLogs={auditLogs}
          user={user}
          emailVerified={emailVerified}
          onNavigate={handleViewChange}
          onDeleteRecord={handleDeleteRecord}
          onEditRecord={handleEditRecord}
          onGrantPermission={handleGrantPermission}
          onViewRecord={handleViewRecord}
          onAddProvider={handleAddProvider}
          onUpdateProvider={handleUpdateProvider}
          onRevokePermission={handleRevokePermission}
          onOpenUpload={() => setShowNewRecordModal(true)}
          onOpenUpdateProfile={() => setShowUpdateProfile(true)}
          onOpenChangePassword={() => setShowChangePassword(true)}
          onResendVerification={handleResendVerification}
        />
      </main>

      {showNewRecordModal && (
        <NewRecordModal
          onClose={() => setShowNewRecordModal(false)}
          onSubmit={handleUploadModalSubmit}
          providers={providersList}
        />
      )}

      {editingRecord && (
        <EditRecordModal
          record={editingRecord}
          onClose={() => setEditingRecord(null)}
          onSubmit={handleUpdateRecord}
        />
      )}
      
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
        isLoading={confirmModal.isLoading}
      />

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onLogout={handleLogout}
      />

      <UpdateProfileModal
        isOpen={showUpdateProfile}
        onClose={() => setShowUpdateProfile(false)}
        user={user}
        onUpdated={(updated) => setUser(updated)}
      />

      <LegalFooter />
    </div>
  );
}
